import { useMantineTheme } from '@mantine/core';
import { IconArrowUpRight } from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';

import { Button, ButtonLink } from '~/domains/design-system/Button';
import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import { IconMarketplace } from '~/domains/design-system/icons/IconMarketplace';
import LogoMarketplace from '~/domains/design-system/vectors/LogoMarketplace';

import { formatSharePercent } from '../../utils/formatSharePercent';
import { holdingHref } from '../../utils/marketplaceHrefs';
import type { EntryOwnership } from '../../utils/resolveEntryOwnership';
import {
  isMarketplaceAcquisition,
  isMarketplaceOwnership,
} from '../../utils/resolveEntryOwnership';
import {
  resolveEntryNameDisplay,
  resolveEntryNameLabel,
} from '../../utils/resolveEntryNameDisplay';
import PickControlIndicator from '../PickControlIndicator/PickControlIndicator';

import EntryShareholderRow from './EntryShareholderRow';
import {
  MARKETPLACE_PILL_ICON_SIZE,
  VIEW_ENTRY_ICON_SIZE,
  useStyles,
} from './EntryOwnershipSheet.styles';

export type EntryOwnershipSheetProps = {
  contestId: string;
  entryId: string;
  entryName?: string;
  entryOrder?: number;
  ownership: EntryOwnership;
  isOpen?: boolean;
  isClosing?: boolean;
  onClose: () => void;
};

function MarketplacePill() {
  const { classes } = useStyles();
  const { t } = useTranslation('contest');
  const theme = useMantineTheme();

  return (
    <div className={classes.marketplacePill}>
      <IconMarketplace
        size={MARKETPLACE_PILL_ICON_SIZE}
        backgroundColor={theme.other.color.marketplace.gradientFrom}
      />
      <Text variant="subhead-small">
        {t('gamecenter.common.entryOwnership.boughtOnMarketplace')}
      </Text>
      <LogoMarketplace />
    </div>
  );
}

function OwnershipSummary({
  shareBps,
  isPickController,
}: {
  shareBps: number;
  isPickController: boolean;
}) {
  const { classes, cx } = useStyles();
  const { t } = useTranslation('contest');

  return (
    <div className={classes.ownership}>
      <div className={classes.ownershipCell}>
        <Text variant="subhead-medium">{formatSharePercent(shareBps)}</Text>
        <Text variant="subhead-small" className={classes.ownershipLabel}>
          {t('gamecenter.common.entryOwnership.youOwn')}
        </Text>
      </div>
      <div className={classes.ownershipDivider} />
      <div className={classes.ownershipCell}>
        <div className={classes.ownershipValue}>
          <PickControlIndicator isPickController={isPickController} tone="strong" />
          <Text
            variant="subhead-medium"
            className={cx(isPickController ? classes.pickControlYes : classes.pickControlNo)}
          >
            {t(
              isPickController
                ? 'gamecenter.common.entryOwnership.pickControlYes'
                : 'gamecenter.common.entryOwnership.pickControlNo'
            )}
          </Text>
        </div>
        <Text variant="subhead-small" className={classes.ownershipLabel}>
          {t('gamecenter.common.entryOwnership.pickControl')}
        </Text>
      </div>
    </div>
  );
}

function EntryOwnershipSheet({
  contestId,
  entryId,
  entryName,
  entryOrder,
  ownership,
  isOpen,
  isClosing,
  onClose,
}: EntryOwnershipSheetProps) {
  const { classes } = useStyles();
  const { t } = useTranslation('contest');

  const { currentShareholder, type } = ownership;
  const nameDisplay = resolveEntryNameDisplay({
    entryName,
    entryOrder,
    isMultiOwner: ownership.isMultiOwner,
    isPickController: ownership.isViewerPickController,
    isEntryOriginator: ownership.isViewerEntryOriginator,
    isViewerEntry: ownership.isCurrentUserEntry,
    isPartialEntry: ownership.isPartialEntry,
  });

  const isMarketplaceEntry = isMarketplaceOwnership(type);
  const showMarketplacePill =
    isMarketplaceEntry && isMarketplaceAcquisition(currentShareholder?.acquiredVia);
  const showOwnershipSummary = isMarketplaceEntry && !!currentShareholder;
  const showPickController = !isMarketplaceEntry || currentShareholder?.isPickController === false;
  const isSoleShareholder = ownership.shareholders.length === 1 && !!currentShareholder;

  return (
    <Dialog
      isOpen={isOpen}
      isClosing={isClosing}
      onClose={onClose}
      type={{ desktop: 'modal', mobile: 'drawer' }}
    >
      <DialogBody className={classes.body}>
        <div className={classes.content}>
          <div className={classes.title}>
            <Title tag="h2" variant="subhead-x-large">
              {resolveEntryNameLabel(nameDisplay, t)}
            </Title>
            {nameDisplay.entryOrder !== undefined && (
              <Text variant="subhead-small" className={classes.entryOrder}>
                ({nameDisplay.entryOrder})
              </Text>
            )}
          </div>

          {showMarketplacePill && <MarketplacePill />}

          {showOwnershipSummary && (
            <OwnershipSummary
              shareBps={currentShareholder.shareBps}
              isPickController={currentShareholder.isPickController}
            />
          )}

          {!isSoleShareholder && (
            <div className={classes.shareholders}>
              {ownership.shareholders.map((shareholder) => (
                <EntryShareholderRow
                  key={shareholder.user.id}
                  shareholder={shareholder}
                  contestId={contestId}
                  entryId={entryId}
                  showOriginalOwner={isMarketplaceEntry}
                  showPickController={showPickController}
                  onNavigate={onClose}
                />
              ))}
            </div>
          )}

          <div className={classes.actions}>
            {isMarketplaceEntry && (
              <ButtonLink
                href={holdingHref(entryId)}
                variant="primary-fill"
                size="medium"
                fullWidth
                onClick={onClose}
              >
                <span className={classes.viewEntryLabel}>
                  {t('gamecenter.common.entryOwnership.viewEntry')}
                  <IconArrowUpRight size={VIEW_ENTRY_ICON_SIZE} />
                </span>
              </ButtonLink>
            )}
            <Button variant="tertiary-fill" size="medium" fullWidth onClick={onClose}>
              {t('gamecenter.common.entryOwnership.close')}
            </Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

export default EntryOwnershipSheet;
